import { lazy, Suspense, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { isProblemError } from '../../api/client';
import { useGetMe } from '../../api/generated/identity/identity';
import {
  getGetRoomStateQueryKey,
  useGetRoomState,
  useLeaveRoom,
  useRequestRoomAdmission,
} from '../../api/generated/room/room';
import type { RoomPhase } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { DeviceCheck } from './DeviceCheck';
import { sfuEndpointOf, type SfuEndpoint, type SfuRefusal } from './sfuEndpoint';
import { StudentWaiting } from './StudentWaiting';
import { TeacherAntechamber } from './TeacherAntechamber';
import styles from './room.module.css';

/**
 * O LiveKit entra por `lazy` porque `livekit-client` e o maior pacote do
 * aplicativo e nenhuma outra tela precisa dele. Carregar so na admissao tambem
 * mantem o modulo fora do grafo de quem apenas passa pela rota — o portao de
 * MFA percorre todas as rotas e nao tem por que instanciar WebRTC.
 */
const LiveRoom = lazy(() => import('./LiveRoom').then((m) => ({ default: m.LiveRoom })));

/**
 * O que a tela conseguiu obter da admissao.
 *
 * `live` carrega endereco E token juntos porque nao existe sala conectavel com
 * um sem o outro; guardar `endpoint: SfuEndpoint | null` ao lado de um
 * `token: string | null` permitiria escrever a combinacao que monta o LiveKit
 * pela metade. `attempt` existe para remontar o componente a cada admissao
 * nova: reconectar com token novo exige instancia nova de `Room`.
 */
type Session =
  | { readonly kind: 'idle' }
  | {
      readonly kind: 'live';
      readonly attempt: number;
      readonly endpoint: SfuEndpoint;
      readonly token: string;
    }
  | { readonly kind: 'refused'; readonly reason: Refusal }
  | { readonly kind: 'misconfigured'; readonly refusal: SfuRefusal };

/**
 * Por que a admissao recusou — e nao "se" ela recusou.
 *
 * As quatro primeiras sao respostas DECLARADAS do contrato, e cada uma manda a
 * pessoa fazer uma coisa diferente: `waiting` e esperar (a aula ainda nao abriu,
 * ou o professor ainda nao foi admitido), `denied` e ir embora (sem captura a
 * aula nao acontece), `ended` e ir embora tambem, mas porque a aula JA
 * ACONTECEU, e `media` e avisar a escola (o SFU nao esta provisionado). Um
 * booleano `mediaMissing` colapsava todas em "Nao foi possivel concluir. Tente
 * de novo em instantes" — o conselho errado para todas, e a frase que a aluna em
 * espera lia enquanto o servidor dizia, por escrito, que ela devia esperar.
 */
type Refusal = 'waiting' | 'denied' | 'ended' | 'media' | 'unknown';

/**
 * As recusas em que TENTAR DE NOVO nunca muda a resposta.
 *
 * `denied` e `ended` sao definitivas — a captura nao vai existir, ou a aula ja
 * terminou —, e oferecer "entrar na sala" nas duas e uma promessa falsa: o
 * proximo clique devolve o mesmo 409 para sempre. As outras ganham o botao,
 * porque nelas o clique seguinte pode ter outro desfecho: o professor entra, o
 * SFU sobe, a rede volta.
 */
const FINAL_REFUSALS = new Set<Refusal>(['denied', 'ended']);

/**
 * Le a FASE que o 409 carrega em `detail`.
 *
 * `api/internal/room/handler.go` monta QUATRO recusas por estado com a fase
 * literal do enum `RoomPhase` — `closed`, `waiting_for_teacher`, `ended` e
 * `denied` — e escreve o motivo no proprio comentario: "o cliente precisa
 * distinguir 'espere, ainda vai comecar' de 'acabou, nao vai comecar', e as duas
 * sao 409". Sem ler o `detail` so restaria adivinhar pelo titulo, que e prosa e
 * muda.
 *
 * As quatro sao lidas, e nao duas. Ler so `denied` empilhava `ended` em
 * `waiting`: a aluna que abrisse a sala de uma aula JA TERMINADA recebia
 * "Aguardando admissao — voce entrara na aula quando o professor for admitido" e
 * um botao de entrar, para uma aula que nunca mais abre. O proprio handler avisa
 * disso por escrito: "o cliente que repete o pedido enquanto espera ficaria
 * repetindo para sempre um pedido que nunca sera aceito".
 *
 * `closed` continua em `waiting` de proposito — "a aula ainda nao comecou" e
 * exatamente o caso em que esperar e o conselho certo e o clique seguinte pode
 * dar certo. Fase desconhecida cai em `waiting` pelo mesmo motivo: 409 nesta
 * porta e sempre recusa por ESTADO da sala, e mandar esperar e o unico conselho
 * que nao fecha a aula de quem ainda podia entrar.
 */
function refusalOf(error: unknown): Refusal {
  if (!isProblemError(error)) return 'unknown';
  if (error.status === 503) return 'media';
  if (error.status !== 409) return 'unknown';
  if (error.detail === 'denied') return 'denied';
  if (error.detail === 'ended') return 'ended';
  return 'waiting';
}

/** De quanto em quanto tempo a sala viva reconfere a propria fase. */
export const ROOM_POLL_MS = 15_000;

/**
 * A cadencia com que o estado da sala e reconsultado — `false` quando nao ha
 * mais o que reconsultar.
 *
 * A consulta nao tinha cadencia nenhuma, e os defaults do cliente sao
 * `refetchOnWindowFocus: false` com `staleTime: 30_000` (app/queryClient.ts):
 * com o componente montado, `room.phase` ficava CONGELADO no valor que chegou
 * quando a pagina abriu. Duas promessas do arquivo morriam ai. A guarda de
 * `denied` diz, logo abaixo, que a recusa vem "antes de qualquer outra coisa,
 * inclusive de uma sessao ja no ar" — mas guarda em cima de dado congelado nunca
 * dispara, e o video seguia no ar depois de a fase dizer o contrario, que e
 * interacao professor-aluno fora de gravacao. E a aluna em espera nunca ficava
 * sabendo que o professor chegou: so descobria clicando de novo, no chute.
 *
 * `denied` e `ended` nao sao reconsultados: as duas sao fases terminais no
 * servidor, e repetir a leitura so gastaria requisicao numa tela que ja nao tem
 * saida. A alternativa recusada foi um `refetchInterval` fixo e sempre ligado —
 * ele deixaria a aba de uma aula encerrada batendo na API para sempre.
 */
export function roomPollInterval(phase: RoomPhase | undefined): number | false {
  if (phase === undefined) return false;
  return phase === 'denied' || phase === 'ended' ? false : ROOM_POLL_MS;
}

export function RoomPage() {
  const { t } = useTranslation();
  const { occurrenceId = '' } = useParams();
  const meQuery = useGetMe();
  const me = meQuery.data;
  const queryClient = useQueryClient();
  const roomQuery = useGetRoomState(occurrenceId, {
    query: {
      enabled: !!occurrenceId,
      refetchInterval: (query) => roomPollInterval(query.state.data?.phase),
    },
  });
  const admission = useRequestRoomAdmission();
  const leave = useLeaveRoom();
  const [deviceOk, setDeviceOk] = useState(false);
  const [session, setSession] = useState<Session>({ kind: 'idle' });

  const page = (children: ReactNode) => (
    <div className={styles.page}>
      <h1>{t('room.title')}</h1>
      {children}
    </div>
  );

  if (!me) return null;

  /*
    A LEITURA DO ESTADO DA SALA PODE FALHAR, e ate aqui falhar era ficar em
    branco: `!roomQuery.data` devolvia `null` e a pessoa recebia uma pagina sem
    titulo, sem mensagem e sem nada em que clicar — indistinguivel de uma tela
    que ainda esta carregando, de uma rota errada e de um navegador travado.
    `getRoomState` recusa com 403 quem nao e da aula e com 404 a ocorrencia que
    nao existe; nas duas, e no 5xx, o que a tela deve e continuar sendo a tela.
  */
  if (roomQuery.isError) {
    return page(<Alert tone="error">{t('error.loadFailed')}</Alert>);
  }
  if (!roomQuery.data) {
    return page(<p role="status">{t('app.loading')}</p>);
  }

  const room = roomQuery.data;
  const isStudent = me.role === 'student';
  const isTeacher = me.role === 'teacher';

  const enter = (): void => {
    admission.mutate(
      { occurrenceId },
      {
        onSuccess: (granted) => {
          // A RESPOSTA E USADA, e este e o defeito que esta tarefa fecha: ate
          // aqui `onSuccess` so ligava um booleano e o token ia para o lixo.
          const endpoint = sfuEndpointOf(granted);
          setSession((previous) =>
            endpoint.ok
              ? {
                  kind: 'live',
                  attempt: (previous.kind === 'live' ? previous.attempt : 0) + 1,
                  endpoint: endpoint.endpoint,
                  token: granted.token,
                }
              : { kind: 'misconfigured', refusal: endpoint.refusal },
          );
        },
        onError: (error) => {
          // 503 nao e "erro inesperado": o contrato o reserva para midia
          // indisponivel, e e o que o backend responde enquanto LiveKit, TURN e
          // Egress seguem sem provisionamento (§15.3). 409 tambem nao e: ele
          // carrega a fase da sala e diz se e para esperar ou para ir embora.
          setSession({ kind: 'refused', reason: refusalOf(error) });
        },
        // BATER NA PORTA E A MELHOR NOTICIA QUE A TELA TEM SOBRE A SALA, e ate
        // aqui ela era jogada fora: a admissao respondia com a fase corrente — de
        // sucesso ou de recusa — e o `roomQuery` continuava servindo, do cache, a
        // fase que chegou quando a pagina abriu. Invalidar no `onSettled`, e nao
        // so no sucesso, porque e justamente a recusa que costuma trazer a fase
        // nova (a sala que passou a `denied` enquanto a pessoa esperava).
        onSettled: () => {
          void queryClient.invalidateQueries({ queryKey: getGetRoomStateQueryKey(occurrenceId) });
        },
      },
    );
  };

  const goLive = (
    <Button variant="primary" loading={admission.isPending} onClick={enter}>
      {t('room.enterRoom')}
    </Button>
  );

  // A recusa vem ANTES de qualquer outra coisa, inclusive de uma sessao ja no
  // ar. `denied` significa captura indisponivel, e o desenho e explicito: sem
  // captura a aula nao acontece. Manter o video no ar enquanto a fase diz o
  // contrario seria interacao professor-aluno fora de gravacao.
  if (room.phase === 'denied') {
    return page(<Alert tone="error">{t('room.admissionDenied')}</Alert>);
  }

  if (session.kind === 'live') {
    return page(
      <Suspense fallback={<p role="status">{t('room.connecting')}</p>}>
        <LiveRoom
          key={session.attempt}
          endpoint={session.endpoint}
          token={session.token}
          reconnecting={admission.isPending}
          onReconnect={enter}
          onLeave={() =>
            leave.mutate({ occurrenceId }, { onSuccess: () => setSession({ kind: 'idle' }) })
          }
        />
      </Suspense>,
    );
  }

  /*
    A AULA JA ACABOU, e a tela para de oferecer entrada.

    Vem depois da sessao ao vivo de proposito: quem esta na aula quando o
    servidor vira a fase nao tem o video arrancado por uma leitura de fundo — a
    sala ao vivo termina pela desconexao do proprio SFU. Antes disso, porem,
    ninguem entra: teste de equipamento e botao de entrar numa aula encerrada sao
    dois convites que so podem terminar em 409.

    `schedule.status.completed` e emprestado, e o emprestimo esta declarado: o
    catalogo nao tem frase propria para "esta aula ja terminou" e o catalogo nao e
    desta fatia. A chave existe nos tres idiomas e diz a verdade; a informacao que
    fecha o assunto e a AUSENCIA do botao de entrar. Ver `fronteira`.
  */
  if (room.phase === 'ended') {
    return page(<Alert tone="warning">{t('schedule.status.completed')}</Alert>);
  }

  /*
    A recusa da admissao, com a resposta do servidor lida ate o fim.

    `denied` e `ended` nao ganham "entrar na sala": numa a captura nao vai
    acontecer, na outra a aula ja aconteceu, e nas duas repetir a chamada devolve
    o mesmo 409. As outras ganham, porque nelas o clique seguinte pode ter outro
    desfecho — o professor entra, o SFU sobe, a rede volta.

    `waiting` mostra a MESMA antessala que a aluna ja veria pela fase da sala.
    Nao e redundancia: sem isto o pedido de admissao trocava a antessala por um
    "Nao foi possivel concluir. Tente de novo em instantes" — a tela dizia que
    algo quebrou no exato momento em que o servidor dizia que estava tudo certo
    e era so esperar.
  */
  if (session.kind === 'refused') {
    if (session.reason === 'waiting') {
      return page(
        <>
          <StudentWaiting />
          {goLive}
        </>,
      );
    }
    return page(
      <>
        <Alert tone={session.reason === 'ended' ? 'warning' : 'error'}>
          {session.reason === 'denied'
            ? t('room.admissionDenied')
            : session.reason === 'ended'
              ? t('schedule.status.completed')
              : session.reason === 'media'
                ? t('room.mediaUnavailable')
                : t('error.unexpected')}
        </Alert>
        {FINAL_REFUSALS.has(session.reason) ? null : goLive}
      </>,
    );
  }

  // O servidor respondeu 201, mas o `serverUrl` nao e endereco. A tela NOMEIA a
  // pendencia e mostra o que chegou, em vez de ficar em branco ou estourar
  // dentro do `livekit-client` — e nao oferece "entrar" de novo, porque repetir
  // a mesma chamada devolve o mesmo valor.
  if (session.kind === 'misconfigured') {
    return page(
      <Alert tone="error">
        <p>{t('room.mediaAddressInvalid')}</p>
        <p>
          {t('room.mediaAddressReceived')}
          {': '}
          <code data-testid="sfu-endpoint-received">{session.refusal.received}</code>
        </p>
      </Alert>,
    );
  }

  // O teste de equipamento de verdade, que sonda camera, microfone e rede. A
  // versao anterior desta tela tinha tres botoes que apenas marcavam "pronto"
  // sem medir nada — teatro de verificacao.
  if (!deviceOk) {
    return page(<DeviceCheck onReady={() => setDeviceOk(true)} />);
  }

  if (isStudent && room.phase === 'waiting_for_teacher') {
    return page(
      <>
        <StudentWaiting />
        {goLive}
      </>,
    );
  }

  if (isTeacher && room.phase !== 'capture_confirmed') {
    return page(
      <>
        <TeacherAntechamber room={room} />
        {goLive}
      </>,
    );
  }

  return page(
    <>
      <Alert tone="warning">{t('room.screenShareWarning')}</Alert>
      {goLive}
    </>,
  );
}
